import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { marketplaceListingReportService } from '../services/api';

interface ReportListingModalProps {
    isOpen: boolean;
    onClose: () => void;
    listingId: string | number | null;
    onSuccess: () => void;
}

const ReportListingModal: React.FC<ReportListingModalProps> = ({ isOpen, onClose, listingId, onSuccess }) => {
    const { t } = useTranslation();
    const [reason, setReason] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');

    if (!isOpen || !listingId) return null;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSubmitting(true);
        setError('');

        try {
            await marketplaceListingReportService.createReport(listingId, { reason });
            setReason('');
            onSuccess();
            onClose();
        } catch (err: any) {
            console.error('Error reporting listing:', err);
            setError(err.response?.data?.message || 'Failed to submit report');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-md w-full p-6 shadow-xl">
                <h2 className="text-xl font-bold mb-4">{t('marketplace.reportListing')}</h2>

                <p className="mb-4 text-gray-600 text-sm">
                    {t('marketplace.reportListingDesc', 'Please explain why this listing should be closed (e.g., Seller inactive, Item sold).')}
                </p>

                <form onSubmit={handleSubmit}>
                    <textarea
                        className="w-full border border-gray-300 rounded-lg p-3 mb-4 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                        placeholder={t('marketplace.reasonPlaceholder', 'Reason for reporting...')}
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        rows={4}
                        required
                    />

                    {error && <p className="text-red-500 text-sm mb-4">{error}</p>}

                    <div className="flex justify-end gap-2">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                            disabled={submitting}
                        >
                            {t('common.cancel')}
                        </button>
                        <button
                            type="submit"
                            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
                            disabled={submitting}
                        >
                            {submitting ? t('common.submitting') : t('common.report')}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default ReportListingModal;
